import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Activity, Lock, ArrowRight, Moon, Sun } from 'lucide-react';

// Página pública de estado dos serviços (/status) - acesso por password partilhada,
// não pela conta de utilizador (ver auth-service/security.py). Fica fora do
// Layout/ProtectedRoute (sem sidebar), mas segue o mesmo estilo visual do resto da
// dashboard (cores, cartões, tipografia) para não parecer uma página à parte.

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const TOKEN_KEY = 'status_token';
const POLL_MS = 10000;

interface ServiceStatus {
  name: string;
  status: 'up' | 'down';
  detail?: string | null;
}

const StatusPage: React.FC = () => {
  const [isDark, setIsDark] = useState(() => localStorage.getItem('theme') === 'dark');
  useEffect(() => {
    if (isDark) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const [token, setToken] = useState<string | null>(() => sessionStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authenticating, setAuthenticating] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const [services, setServices] = useState<ServiceStatus[] | null>(null);
  const [checkedAt, setCheckedAt] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState('');

  useEffect(() => {
    if (!token) passwordInputRef.current?.focus();
  }, [token]);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API_BASE}/status/services`, { headers: { Authorization: `Bearer ${token}` } });
      setServices(data.services);
      setCheckedAt(data.checked_at);
      setFetchError('');
    } catch (err: any) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setServices(null);
      } else {
        setFetchError('Não consegui atualizar agora. Vou tentar outra vez.');
      }
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [token, load]);

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthenticating(true);
    setAuthError('');
    try {
      const { data } = await axios.post(`${API_BASE}/status/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
      setPassword('');
    } catch {
      setAuthError('Password incorreta.');
    } finally {
      setAuthenticating(false);
    }
  };

  const allUp = services != null && services.every(s => s.status === 'up');
  const downCount = services ? services.filter(s => s.status === 'down').length : 0;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0b0f1a] flex items-center justify-center p-4 transition-colors duration-300 relative">
      <button
        onClick={() => setIsDark(d => !d)}
        className="absolute top-5 right-5 p-2.5 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-white dark:hover:bg-slate-800 transition-colors"
        title={isDark ? 'Modo claro' : 'Modo escuro'}
      >
        {isDark ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      {!token ? (
        <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-3xl shadow-sm p-8 sm:p-10 border border-slate-200 dark:border-slate-700">
          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-blue-600 rounded-2xl mx-auto flex items-center justify-center text-white mb-4">
              <Activity size={26} />
            </div>
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-white tracking-tight">Estado dos serviços</h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">Só precisas da password de acesso.</p>
          </div>

          <form onSubmit={handleUnlock} className="space-y-5">
            {authError && (
              <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-xl text-sm text-center font-medium border border-red-100 dark:border-red-900/30">
                {authError}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  ref={passwordInputRef}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-4 focus:ring-blue-500/10 outline-none transition-all font-medium text-slate-700 dark:text-white placeholder:text-slate-400 placeholder:font-normal"
                  placeholder="••••••••"
                  autoComplete="off"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={authenticating}
              className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 dark:bg-blue-600 hover:bg-black dark:hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition-all active:scale-[0.98] disabled:opacity-50"
            >
              <span>{authenticating ? 'A verificar…' : 'Entrar'}</span> <ArrowRight size={18} />
            </button>
          </form>
        </div>
      ) : (
        <div className="max-w-lg w-full bg-white dark:bg-slate-800 rounded-3xl shadow-sm p-6 sm:p-8 border border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2.5 bg-blue-600/10 text-blue-600 rounded-xl shrink-0"><Activity size={18} /></div>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight leading-tight">
                {services == null ? 'A verificar…' : allUp ? 'Tudo a funcionar' : `${downCount} serviço${downCount > 1 ? 's' : ''} em baixo`}
              </h1>
              <p className="text-xs text-slate-400">
                {checkedAt ? `última verificação às ${new Date(checkedAt).toLocaleTimeString('pt-PT')}` : 'a ligar ao servidor...'}
              </p>
            </div>
            <span className={`ml-auto w-2 h-2 rounded-full shrink-0 ${services == null ? 'bg-slate-300' : allUp ? 'bg-emerald-500 animate-pulse' : 'bg-red-500 animate-pulse'}`} />
          </div>

          {fetchError && (
            <p className="text-xs text-red-500 mt-3">{fetchError}</p>
          )}

          <div className="mt-6 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
            {services == null ? (
              <div className="px-5 py-8 text-center text-sm text-slate-400">A ligar ao servidor...</div>
            ) : (
              services.map((s, i) => (
                <div
                  key={s.name}
                  className={`flex items-center gap-3 px-5 py-3.5 ${i > 0 ? 'border-t border-slate-100 dark:border-slate-700/50' : ''}`}
                >
                  <span className={`shrink-0 w-2 h-2 rounded-full ${s.status === 'up' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200 flex-1 min-w-0 truncate">{s.name}</span>
                  {s.status === 'down' ? (
                    <span className="text-xs font-semibold text-red-500 shrink-0">em baixo</span>
                  ) : s.detail ? (
                    <span className="text-xs text-slate-400 shrink-0 hidden sm:inline">{s.detail}</span>
                  ) : null}
                </div>
              ))
            )}
          </div>

          <p className="text-xs text-slate-400 text-center mt-5">Atualiza sozinha a cada 10 segundos.</p>
        </div>
      )}
    </div>
  );
};

export default StatusPage;
